import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { farmsByCompanyID } from "graphql/queries";
import { updateFarm } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import { getCompanyName } from "graphql/custom-queries";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import FarmCreateForm from "./FarmCreateForm";
import FarmUpdateForm from "./FarmUpdateForm";

const Farms = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "agro:read",
    "agro:read",
    "agro:read",
  ]);
  const [farms, setFarms] = useState([]);
  const [farmsLoading, setFarmsLoading] = useState(false);
  const [farmDetails, setFarmDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    farmId: null,
    farmName: null,
  });
  const [modalFarm, setModalFarm] = useState({
    visible: false,
    farmId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      farms.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        actions: (
          <>
            <ActionIcon
              name={
                farmDetails?.id === item.id
                  ? strings.farms.hide
                  : strings.farms.show
              }
              icon={
                farmDetails?.id === item.id ? "VisibilityOff" : "Visibility"
              }
              color="primary"
              onClick={() => {
                farmDetails?.id === item.id
                  ? setFarmDetails(null)
                  : setFarmDetails(item);
              }}
            />
            {canUpdate && (
              <ActionIcon
                name={strings.farms.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <ActionIcon
                name={strings.farms.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    farmId: item.id,
                    farmName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [farms, farmDetails?.id]
  );

  const onNext = () => {
    fetchFarms(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchFarms(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchFarms(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalFarm({ visible: true, action, farmId: id });
  const onSuccessFarm = () => {
    setModalFarm({ visible: false });
    fetchFarms();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNamesByFarms = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      setFarms((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
        };

        return updatedList;
      });
    });
  };

  async function fetchFarms(token, isNext = true, keywords) {
    const nameOperation = farmsByCompanyID;
    const nameOperationStr = "farmsByCompanyID";
    try {
      setFarmsLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      const farmsData = await API.graphql(
        graphqlOperation(nameOperation, {
          companyID: searchBy.company.id || me.companyID,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                    {
                      location: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const farmsItems = farmsData.data[nameOperationStr].items;
      setFarms(farmsItems);
      getNamesByFarms(farmsItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: farmsData.data[nameOperationStr].nextToken,
        };
      });
      setFarmsLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setFarmsLoading(false);
    } finally {
      setFarmsLoading(false);
    }
  }
  async function farmDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateFarm, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateFarm.id;
      setFarms(farms.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        farmId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchFarms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.branch.id, searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "25%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "25%" },
    },
    {
      key: "location",
      label: strings.table.headers.location,
      _style: { width: "25%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "25%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.farms.addFarm}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {farmsLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!farmDetails?.id} style={styles.collapseCard}>
          <CCard key={farmDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{farmDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.description}:</b> {farmDetails?.description}
              </p>
              <p className="text-muted">
                <b>{strings.details.location}:</b> {farmDetails?.location}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(farmDetails?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.farmName,
        })}
        onCancel={() => setModalDetails({ visible: false, farmId: null })}
        onConfirm={() => farmDelete(modalDetails.farmId)}
      />
      {modalFarm.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalFarm.visible}
          onClose={() => setModalFarm({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalFarm.action === "create" && strings.modalFarm.addTitle}
              {modalFarm.action === "update" && strings.modalFarm.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalFarm.action === "create" && (
                <FarmCreateForm onSuccess={onSuccessFarm} />
              )}
              {modalFarm.action === "update" && (
                <FarmUpdateForm
                  farmId={modalFarm.farmId}
                  onSuccess={onSuccessFarm}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Fincas",
    successDeleted: "Finca eliminada con éxito",
    farms: {
      addFarm: "Agregar Finca",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      params: "Parámetros",
      readPhotos: "Ver Fotos",
      docs: "Documentos",
    },
    responses: {
      errorFetch: "Error al consultar finca",
    },
    table: {
      headers: {
        name: "Nombre",
        location: "Ubicación",
        companyName: "Empresa",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles de la Finca",
      description: "Descripción",
      location: "Ubicación",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar la finca {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalFarm: {
      addTitle: "Agregar Finca",
      updateTitle: "Actualizar Finca",
      readPhotosTitle: "Fotos de la Finca",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Farms;
